import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Logo } from '#app/components/logo.tsx'
import { openConsentSettings } from '#app/utils/consent-settings.ts'

type FooterLink =
	| { label: string; href: string }
	| { label: string; to: string }
	| { label: string; onClick: () => void }

function FooterLinkItem({ link }: { link: FooterLink }) {
	const className =
		'text-muted-foreground hover:text-brand transition-colors duration-[var(--duration-fast)] ease-out'
	if ('onClick' in link) {
		// The "Privacy settings" re-open control: an action, not navigation, so a
		// button — emphasized (brand + underline) beside the plain legal links.
		return (
			<button
				type="button"
				onClick={link.onClick}
				className="text-brand text-left underline transition-colors duration-[var(--duration-fast)] ease-out hover:text-brand/80"
			>
				{link.label}
			</button>
		)
	}
	return 'to' in link ? (
		<Link to={link.to} className={className}>
			{link.label}
		</Link>
	) : (
		<a href={link.href} className={className}>
			{link.label}
		</a>
	)
}

/**
 * Branded marketing footer: a brand identity column plus nav / legal link
 * columns. Collapses to two columns under 820px (`sm`). Section-anchor labels
 * (Work/Services/Pricing/FAQ) reuse the shared navbar chrome in `common.nav`;
 * the footer-specific labels live in the `public.landing.footer` copy (ADR-074).
 */
export function MarketingFooter() {
	const { t } = useTranslation('public')
	const { t: tc } = useTranslation('common')

	const columns: Array<{ heading: string; links: FooterLink[] }> = [
		{
			heading: t('landing.footer.columns.studio'),
			links: [
				{ label: tc('nav.sections.work'), href: '#work' },
				{ label: tc('nav.features'), to: '/features' },
				{ label: tc('nav.sections.pricing'), href: '#pricing' },
			],
		},
		{
			heading: t('landing.footer.columns.resources'),
			links: [
				{ label: tc('nav.faq'), to: '/faq' },
				{ label: t('landing.footer.links.about'), to: '/about' },
				{ label: t('landing.footer.links.support'), to: '/support' },
			],
		},
		{
			heading: t('landing.footer.columns.legal'),
			links: [
				{ label: t('landing.footer.links.impressum'), to: '/impressum' },
				{ label: t('landing.footer.links.datenschutz'), to: '/datenschutz' },
				{ label: t('landing.footer.links.terms'), to: '/tos' },
				{ label: tc('consent.footerLink'), onClick: openConsentSettings },
			],
		},
	]

	return (
		<footer className="border-border bg-background border-t">
			<div className="container grid grid-cols-2 gap-8 py-12 sm:grid-cols-4">
				<div className="col-span-2 flex flex-col gap-3 sm:col-span-1">
					<Logo />
					<p className="text-muted-foreground text-body-xs text-pretty">
						{t('landing.footer.blurb')}
					</p>
				</div>
				{columns.map((column) => (
					<nav
						key={column.heading}
						aria-label={column.heading}
						className="flex flex-col gap-3 text-body-xs"
					>
						<h2 className="text-foreground/80 font-medium">{column.heading}</h2>
						<ul className="flex flex-col gap-2">
							{column.links.map((link) => (
								<li key={link.label}>
									<FooterLinkItem link={link} />
								</li>
							))}
						</ul>
					</nav>
				))}
			</div>
		</footer>
	)
}
